import { useState } from "react";
import { ListMusic, X, Loader2, Download, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

export default function SetlistBar({ selected, onClear, onReorder }) {
  const [name, setName] = useState("My Setlist");
  const [includeChords, setIncludeChords] = useState(true);
  const [includeLyrics, setIncludeLyrics] = useState(false);
  const [transpose, setTranspose] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const ids = selected.map((s) => s.id);

  const move = (from, to) => {
    if (to < 0 || to >= ids.length || from === to) return;
    const next = [...ids];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onReorder(next);
  };

  const onDrop = (to) => {
    if (dragIdx === null) return;
    move(dragIdx, to);
    setDragIdx(null);
  };

  const exportSetlist = async () => {
    if (!includeChords && !includeLyrics) return toast.error("Pick chords, lyrics, or both.");
    setBusy(true);
    try {
      const res = await api.post(
        "/songs/export/setlist",
        { name, song_ids: selected.map((s) => s.id), include_chords: includeChords, include_lyrics: includeLyrics, transpose },
        { responseType: "blob" }
      );
      const slug = (name || "setlist").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_|_$/g, "") || "setlist";
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `${slug}_setlist.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success(`Setlist exported (${selected.length} songs)`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="setlist-bar"
      className="fixed inset-x-0 bottom-[68px] z-30 border-t border-cyan-500/20 bg-[#0B0E14]/97 px-4 py-3 backdrop-blur-xl md:bottom-0 md:left-60"
    >
      <div className="mx-auto max-w-5xl space-y-3">
        {selected.length > 1 && (
          <div data-testid="setlist-order" className="max-h-28 space-y-1.5 overflow-y-auto pr-1">
            {selected.map((s, i) => (
              <div
                key={s.id}
                data-testid={`setlist-item-${s.id}`}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                className={`flex items-center gap-2 rounded-lg border bg-[#131822] px-2.5 py-1.5 ${
                  dragIdx === i ? "border-cyan-400/60 opacity-60" : "border-[#222A38]"
                }`}
              >
                <GripVertical size={15} className="shrink-0 cursor-grab text-slate-500" />
                <span className="w-5 shrink-0 text-center font-mono-sw text-xs font-bold text-cyan-400">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-white">{s.title}</span>
                <span className="hidden shrink-0 truncate text-xs text-slate-500 sm:block">{s.artist}</span>
                <button data-testid={`setlist-up-${s.id}`} disabled={i === 0} onClick={() => move(i, i - 1)} className="shrink-0 rounded p-1 text-slate-400 hover:text-white disabled:opacity-30">
                  <ChevronUp size={15} />
                </button>
                <button data-testid={`setlist-down-${s.id}`} disabled={i === selected.length - 1} onClick={() => move(i, i + 1)} className="shrink-0 rounded p-1 text-slate-400 hover:text-white disabled:opacity-30">
                  <ChevronDown size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3">
          <ListMusic size={18} className="text-cyan-400" />
          <input
            data-testid="setlist-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Setlist name"
            className="min-w-0 flex-1 rounded-lg border border-[#222A38] bg-[#131822] px-3 py-2 text-sm text-white outline-none focus:border-cyan-500/60"
          />
          <span className="shrink-0 font-mono-sw text-xs text-slate-400" data-testid="setlist-count">{selected.length} selected</span>
          <button data-testid="setlist-clear" onClick={onClear} className="shrink-0 rounded-full p-1.5 text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle testid="setlist-chords" active={includeChords} onClick={() => setIncludeChords((v) => !v)}>Chords</Toggle>
          <Toggle testid="setlist-lyrics" active={includeLyrics} onClick={() => setIncludeLyrics((v) => !v)}>Lyrics</Toggle>
          <div className="flex items-center gap-1 rounded-full bg-[#131822] px-2 py-1 ring-1 ring-[#222A38]">
            <span className="pl-1 font-mono-sw text-[11px] uppercase text-slate-400">Transpose</span>
            <button data-testid="setlist-trans-down" onClick={() => setTranspose(Math.max(-12, transpose - 1))} className="px-1.5 text-slate-300 hover:text-white">−</button>
            <span className="w-6 text-center font-mono-sw text-xs font-bold text-cyan-400">{transpose > 0 ? `+${transpose}` : transpose}</span>
            <button data-testid="setlist-trans-up" onClick={() => setTranspose(Math.min(12, transpose + 1))} className="px-1.5 text-slate-300 hover:text-white">+</button>
          </div>
          <button
            data-testid="export-setlist-btn"
            disabled={busy || selected.length === 0}
            onClick={exportSetlist}
            className="ml-auto flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-[#0B0E14] transition-colors hover:bg-cyan-400 disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Export Setlist
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
        active ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#131822] text-slate-400 ring-1 ring-[#222A38]"
      }`}
    >
      {children}
    </button>
  );
}
